import { QueryClient } from '@tanstack/react-query'

import * as snugNotifier from 'app/services/snugNotifier'

// TypeScript interfaces for error handling
interface ApiError {
  message?: string
}

// Create QueryClient with custom configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep data for 10 minutes (cacheTime in v4, gcTime in v5)
      cacheTime: 10 * 60 * 1000,
      // Retry failed requests 2 times
      retry: 2,
      // Don't refetch on window focus for onboarding data
      refetchOnWindowFocus: false,
      // Error handling - show error notification
      onError: (error: unknown) => {
        console.error('Query Error:', error)
        const errorMessage =
          error instanceof Error || (error as ApiError)?.message
            ? (error as ApiError).message || (error as Error).message
            : 'An error occurred while fetching data'
        snugNotifier.error(errorMessage)
      },
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      // Error handling for mutations
      onError: (error: unknown) => {
        console.error('Mutation Error:', error)
        const errorMessage =
          error instanceof Error || (error as ApiError)?.message
            ? (error as ApiError).message || (error as Error).message
            : 'An error occurred while updating data'
        snugNotifier.error(errorMessage)
      },
    },
  },
})

// Query Keys - centralized for consistency with proper TypeScript types
export const queryKeys = {
  onboarding: {
    all: ['onboarding'] as const,
    data: (teamGUID: string) =>
      [...queryKeys.onboarding.all, 'data', teamGUID] as const,
    step: (teamGUID: string, stepId: string) =>
      [...queryKeys.onboarding.all, 'step', teamGUID, stepId] as const,
    preferences: (agencyGUID: string) =>
      [...queryKeys.onboarding.all, 'preferences', agencyGUID] as const,
  },
  rentalHistory: {
    all: ['rentalHistory'] as const,
    list: () => [...queryKeys.rentalHistory.all, 'list'] as const,
    detail: (id: string) =>
      [...queryKeys.rentalHistory.all, 'detail', id] as const,
    create: () => [...queryKeys.rentalHistory.all, 'create'] as const,
    update: (id: string) =>
      [...queryKeys.rentalHistory.all, 'update', id] as const,
    delete: (id: string) =>
      [...queryKeys.rentalHistory.all, 'delete', id] as const,
    managers: (searchValue: string) =>
      [...queryKeys.rentalHistory.all, 'managers', searchValue] as const,
  },
} as const
